import { postDeleteAllRows } from "@/api/sheets";
import { useAppNotificationManager } from "@/components/AppProvider/AppNotificationProvider";
import { useDeleteReferredFrom } from "@/domain/ReferredFrom";
import {
  buildTadSheetReferredFromId,
  useDeleteTadSheetReferredFrom,
} from "@/domain/TadSheetReferredFrom";
import { ManagedTransaction } from "@/firebase/firestore";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router";
import { useAppRouteParams } from "../../../../../AppRoutes";
import {
  Sheet,
  useRecursiveDeleteSheet,
  useUpdateSheet,
} from "../../../../../domain/Sheet";
import { useBulkDialogRerenderGuard } from "../../BulkDialogRerenderGuardProvider";
import { ReferredFromState, getDialogMessages } from "../../helper";

/**
 * DialogSheetDeleteSome
 *
 * BulkDialogRerenderGuardProvider 内で使用
 * Firestore 更新による再レンダリングからダイアログ状態を保護する。
 */
export const DialogSheetDeleteSome: React.FC<{
  sheets: Sheet[];
  selectedSheetIds: string[];
  setSelectedSheetIds: React.Dispatch<React.SetStateAction<string[]>>;
  isIncludingReferencedSheets: ReferredFromState;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({
  sheets,
  selectedSheetIds,
  setSelectedSheetIds,
  isIncludingReferencedSheets,
  open,
  setOpen,
}) => {
  const navigate = useNavigate();
  const { organizationId, versionId, sheetId } = useAppRouteParams();
  const recursiveDeleteSheet = useRecursiveDeleteSheet();
  const deleteReferredFrom = useDeleteReferredFrom();
  const deleteTadSheetReferredFrom = useDeleteTadSheetReferredFrom();
  const updateSheet = useUpdateSheet();
  const { showErrorNotification } = useAppNotificationManager();

  const { openBulkDialog, isBulkDialogOpen } = useBulkDialogRerenderGuard();

  const selectedSheets = sheets.filter(({ id }) =>
    selectedSheetIds.includes(id)
  );

  const messages = getDialogMessages({
    isSome: true,
    actionType: "delete",
    targetType: "sheet",
  });

  // NOTE: useBulkDialogの順次処理に伴い、deleteRecursiveSheetDataを踏襲しつつ1件ごとに処理
  const runDelete = useCallback(
    async (sheet: Sheet) => {
      // LDSシートの行削除
      if (sheet.backend === "lds") {
        await postDeleteAllRows({
          organizationId,
          versionId,
          sheetId: sheet.id,
        });
      }

      const deleteQueue = await recursiveDeleteSheet(sheet.id, {});

      // 参照シート / TADS参照シートの場合のみトランザクション必要
      // （referredFrom削除との原子性を保証）
      const { referTo, tadReferTo } = sheet;
      if (referTo || tadReferTo) {
        await ManagedTransaction.runTransaction(async (transaction) => {
          if (referTo) {
            await deleteReferredFrom(
              `${versionId}:${sheet.id}`,
              {
                versionId: referTo.versionId,
                sheetId: referTo.sheetId,
              },
              { transaction }
            );
          }
          // 残すと参照先 TADS の物理削除が 409 で永久にブロックされる
          if (tadReferTo) {
            await deleteTadSheetReferredFrom(
              buildTadSheetReferredFromId(versionId, sheet.id),
              { tadSheetId: tadReferTo.tadSheetId },
              { transaction }
            );
          }
          await deleteQueue.exec(transaction);
        });
      } else {
        await deleteQueue.exec();
      }
    },
    [organizationId, versionId]
  );

  const onCloseHandler = useCallback(
    async (states: Array<Sheet & { status: string }>) => {
      setOpen(false);
      const [successIds, remainingIds] = states.reduce<[string[], string[]]>(
        ([success, remaining], s) => {
          s.status === "success" ? success.push(s.id) : remaining.push(s.id);
          return [success, remaining];
        },
        [[], []]
      );
      setSelectedSheetIds(remainingIds);

      // ソート番号を振り直す（成功した削除がある場合のみ）
      if (successIds.length > 0) {
        const restSheets = sheets.filter(({ id }) => !successIds.includes(id));
        try {
          await ManagedTransaction.runTransaction(async (transaction) => {
            for (const [index, sheet] of restSheets.entries()) {
              if (sheet.sort !== index) {
                await updateSheet(
                  { ...sheet, sort: index },
                  { sheetId: sheet.id },
                  { transaction }
                );
              }
            }
          });
        } catch (error: unknown) {
          showErrorNotification(
            "シートのソート番号の更新に失敗しました",
            error
          );
        }
      }

      // 削除に成功したシートに現在表示中のシートが含まれていたらナビゲート
      if (successIds.includes(sheetId)) {
        navigate(`/organizations/${organizationId}/versions/${versionId}`);
      }
    },
    [sheets, sheetId, organizationId, versionId]
  );

  const bulkDialogArgs = useMemo(() => {
    const displayMessage =
      (isIncludingReferencedSheets === "including"
        ? "他のシートから参照されているシートが含まれるため、表示が破壊されることがあります。\n"
        : "") + messages.confirm.displayMessage;

    return {
      ...messages.confirm,
      displayMessage,
      rows: selectedSheets,
      rowKey: "id" as const,
      run: runDelete,
      isThemeRed: true,
      renderItem: (sheet: Sheet) => <div>{sheet.name}</div>,
      onClose: ({ states }: { states: Array<Sheet & { status: string }> }) =>
        onCloseHandler(states),
    };
  }, [
    messages,
    isIncludingReferencedSheets,
    selectedSheets,
    runDelete,
    onCloseHandler,
  ]);

  const argsRef = useRef(bulkDialogArgs);
  argsRef.current = bulkDialogArgs;

  useEffect(() => {
    if (open && !isBulkDialogOpen) {
      openBulkDialog<"id", Sheet>(argsRef.current);
    }
  }, [open, isBulkDialogOpen, openBulkDialog]);

  return null;
};
